import { capitalize, cn } from '@express-labs/raven-tools';

import React, {
  forwardRef,
  type ElementType,
  type ComponentPropsWithRef,
  type JSXElementConstructor,
  type Ref,
} from 'react';
import s from './Link.scss';
import {
  useHandleOnClickWithIntercept,
  useHandleOnKeyDownWithIntercept,
} from '../helpers/hook';

// polymorphic component with forwarded ref thanks to
// https://www.tsteele.dev/posts/react-polymorphic-forwardref
// https://gist.github.com/kripod/4434e7cecfdecee160026aee49ea6ee8

type IntrinsicAttributes<
  E extends keyof JSX.IntrinsicElements | JSXElementConstructor<any>,
> = JSX.LibraryManagedAttributes<E, ComponentPropsWithRef<E>>;

interface LinkOwnProps<E extends ElementType = ElementType> {
  /** Specify the HTML tag to render for the Link.  The default tag is "a".  Only "button" and "a" are officially supported via CSS. Other tags may require more CSS. */
  as?: E;
  /** Force the link to display as "selected" or "inactive" */
  linkState?: 'pressed' | 'inactive';
  /** Any additional class or classes to further customize the button appearance. */
  className?: string;
  /** Links come in three sizes. */
  size?: 'primary' | 'secondary' | 'body';
  /** The color theme for the link. */
  theme?: 'light' | 'dark';
  /** Child content and/or JSX to render inside the HTML tags. */
  children?: React.ReactNode;
  /* Gives the link a "disabled" appearance however, unlike the html attribute "disabled", users are still able to tab-into a isAccessiblyDisabled Link and click on it. Any onClick or onKeyDown events while isAccessiblyDisabled is true are captured and ignored. */
  isAccessiblyDisabled?: boolean;
  onClick?: (ev: React.MouseEvent<HTMLElement>) => void;
  onKeyDown?: (ev: React.KeyboardEvent<HTMLElement>) => void;
}

export type LinkProps<E extends ElementType> = LinkOwnProps<E> &
  Omit<IntrinsicAttributes<E>, keyof LinkOwnProps>;

type ForwardedLinkComponent = {
  displayName: string;
  <E extends ElementType = 'a'>(props: LinkProps<E>): JSX.Element;
};

const Link = forwardRef(function Link(
  {
    as: Element = 'a',
    children,
    className,
    isAccessiblyDisabled,
    linkState,
    onClick,
    onKeyDown,
    size = 'body',
    theme = 'dark',
    ...restProps
  }: LinkOwnProps,
  ref: Ref<Element>
) {
  const handleOnClick = useHandleOnClickWithIntercept(
    isAccessiblyDisabled,
    onClick
  );

  const handleOnKeyDown = useHandleOnKeyDownWithIntercept(
    isAccessiblyDisabled,
    onKeyDown
  );

  return (
    <Element
      ref={ref}
      className={cn(
        'rvn-ui--link',
        'rvn-brander--exempt',
        s[
          `${Element ?? /* istanbul ignore next - can't get here with Typescript*/ 'a'}Tag`
        ],
        s[`${size}${capitalize(theme)}`],
        isAccessiblyDisabled && s.inactive,
        linkState && s[linkState],
        className
      )}
      onClick={handleOnClick}
      onKeyDown={handleOnKeyDown}
      {...restProps}
    >
      {children}
    </Element>
  );
}) as ForwardedLinkComponent;

Link.displayName = 'Link';

export default Link;
